export type IssueStatus = 'AUTO_FIXED' | 'AWAITING_DECISION' | 'FLAGGED' | 'CANNOT_RESOLVE';
export type IssueSeverity = 'HIGH' | 'MEDIUM' | 'LOW';
export type QuestionStatus = 'OPEN' | 'ANSWERED' | 'APPLIED';
export type AnswerType = 'SINGLE_CHOICE' | 'YES_NO' | 'TEXT_INPUT' | 'MULTI_CHOICE';

export interface AffectedRow {
  row_id: number;
  original_row_number: number;
  field: string;
  original_value: string;
  current_value: string;
  changed: boolean;
}

export interface ValueDistribution {
  value: string;
  count: number;
}

export interface Issue {
  issue_id: string;
  severity: IssueSeverity;
  type: string;
  column: string;
  status: IssueStatus;
  linked_question_id: string | null;
  summary: string;
  business_explanation: string;
  action_taken: string | null;
  action_reason: string | null;
  next_action: string | null;
  affected_count: number;
  affected_rows: AffectedRow[];
  value_distribution: ValueDistribution[] | null;
  phase: string;
  rule_id: string;
  unit_id?: string | null;
  sheet_name?: string | null;
}

export type SubInputType =
  | 'text'
  | 'long_text'
  | 'number'
  | 'boolean'
  | 'select'
  | 'column_select'
  | 'column_multi_select'
  | 'section_select'
  | 'section_multi_select'
  | 'file_select'
  | 'mapping_list'
  | 'replacement_list';

export interface SubInputDefinition {
  key: string;
  label: string;
  type: SubInputType;
  options?: Array<string | { value: string; label: string }>;
  default?: string | number | boolean;
  placeholder?: string;
  required?: boolean;
}

export interface SectionInfo {
  section_id: string;
  heading: string;
  preview?: string;
  length_chars?: number;
  length_words?: number;
}

export interface SectionReplacement {
  section_id: string;
  original_value: string;
}

export interface MappingPair {
  original: string;
  canonical: string;
}

export interface QuestionOption {
  option_id: string;
  label: string;
  description: string;
  preview_statement: string;
  requires_sub_inputs?: boolean;
  sub_inputs?: SubInputDefinition[];
  default_params?: Record<string, any>;
}

export interface Question {
  question_id: string;
  severity: IssueSeverity;
  question_text: string;
  business_context: string;
  columns_affected: string[];
  available_columns?: string[];
  records_affected: number;
  linked_issue_id: string | null;
  status: QuestionStatus;
  answer_type: AnswerType;
  options: QuestionOption[];
  selected_option_id: string | null;
  applied: boolean;
  applied_at: string | null;
  applied_by: string | null;
  unanswered_consequence: string;
  unit_id?: string | null;
  sheet_name?: string | null;
  file_name?: string | null;
  question_type?: string;
  finding?: any;
  finding_kind?: string;
  section_refs?: string[];
  files?: string[];
  claims?: Array<{ file?: string | null; section_id?: string; value?: string; heading?: string | null; context?: string }>;
  candidate_resolutions?: Array<{ label: string; kind: string; details?: string; winner_file?: string; winner_value?: string }>;
  issue_type?: string | null;
}

export interface AutoFixPreviewSummary {
  total: number;
  rowsChanged: number;
  summaryAction: string | null;
}

export interface QualityScore {
  overall: number;
  completeness: number;
  consistency: number;
  validity: number;
  before: number;
  after: number;
  revisedDown?: boolean;
}

export interface PipelinePhaseSubItem {
  icon: 'check' | 'warning' | 'info';
  label: string;
}

export interface PipelinePhaseGroupStat {
  label: string;
  count: number;
  tone: 'green' | 'amber' | 'blue';
}

export interface PipelinePhaseDetailCard {
  title: string;
  items: PipelinePhaseSubItem[];
}

export interface PipelinePhase {
  phase_number: number;
  name: string;
  sub_label?: string;
  status: 'complete' | 'partial' | 'skipped';
  narrative: string;
  fixed_count: number;
  flagged_count: number;
  skipped_count: number;
  sub_items?: PipelinePhaseSubItem[];
  group_stats?: PipelinePhaseGroupStat[];
  pending_count?: number;
  detail_cards?: PipelinePhaseDetailCard[];
}

export interface ChangeLogEntry {
  id: string;
  category: 'auto_fixed' | 'user_decision' | 'flagged';
  title: string;
  description: string;
  phase: string;
  field: string;
  action_type: string;
  count: number;
  details: ChangeLogDetail[];
}

export interface ChangeLogDetail {
  row: number;
  field: string;
  original_value: string;
  current_value: string;
  rule: string;
}

export interface DataPreviewRow {
  row_number: number;
  status: 'changed' | 'flagged' | 'unchanged' | 'removed';
  cells: DataPreviewCell[];
}

export interface DataPreviewCell {
  column: string;
  original_value: string;
  current_value: string;
  changed: boolean;
  flagged: boolean;
}

export interface CleaningResults {
  issues: Issue[];
  questions: Question[];
  qualityScore: QualityScore;
  pipelinePhases: PipelinePhase[];
  changeLog: ChangeLogEntry[];
  summary: string;
  stats: Record<string, any>;
  cleanedFileUrl: string | null;
  cleanedFilePath: string | null;
  cleanedFileName: string;
  originalFileNames: string[];
  pythonCode: string;
  sessionId: number | null;
}

export interface ApplyResult {
  question: Question;
  issue: Issue;
  qualityScore: QualityScore;
}

export interface SheetUnit {
  unit_id: string;
  file_name: string;
  sheet_name: string | null;
  sheet_index: number;
  status: 'complete' | 'failed' | 'partial' | 'unknown';
  partial_clean?: boolean;
  summary?: string;
  cleanedFilePath: string | null;
  cleanedFileS3Key: string | null;
  cleanedFileUrl: string | null;
  cleanedFileName: string | null;
  rows_total: number;
  rows_after: number;
  columns_processed: number;
  stats: Record<string, any>;
  issuesFound: any[];
  stakeholderQuestions: any[];
  canonicalMapping: any[];
  previewBefore: string;
  previewAfter: string;
  qualityScoreBefore: number;
  qualityScoreAfter: number;
  qualityRevisedDown?: boolean;
  cleaningLog: string;
  pythonCode: string;
  error?: string | null;
  stderr_tail?: string | null;
}

export interface RollupStats {
  total_units: number;
  total_sheets: number;
  succeeded_units?: number;
  failed_units?: number;
  total_rows: number;
  total_rows_after: number;
  qualityScoreBefore_weighted: number;
  qualityScoreAfter_weighted: number;
  issues_total: number;
  questions_total: number;
}

export interface FkRelationship {
  relationship_id: string;
  kind: 'foreign_key' | 'master_lookup';
  from_unit_id: string;
  from_column: string;
  to_unit_id: string;
  to_column: string;
  overlap_pct: number;
  child_size: number;
  parent_size: number;
  sample_matches: string[];
  llm_confirmed: boolean;
  reason: string;
}

export interface OrphanFinding {
  finding_id: string;
  relationship_id: string;
  from_unit_id: string;
  from_column: string;
  to_unit_id: string;
  to_column: string;
  orphan_count: number;
  orphan_pct?: number;
  sample_orphan_values: string[];
  sample_orphan_rows: Record<string, any>[];
  severity: 'high' | 'medium' | 'low';
  action_suggested: 'flag' | 'remove' | 'set_null';
  error?: string;
}

export interface MasterViolationFinding {
  finding_id: string;
  relationship_id: string;
  source_unit_id: string;
  source_column: string;
  master_unit_id: string;
  master_column: string;
  violation_count: number;
  violation_pct?: number;
  sample_invalid_values: string[];
  sample_violation_rows: Record<string, any>[];
  severity: 'high' | 'medium' | 'low';
  action_suggested: 'flag' | 'remove' | 'set_null';
  error?: string;
}

export interface CompoundRuleViolation {
  finding_id: string;
  unit_id: string;
  rule_name: string;
  predicate_dsl: string;
  violation_count: number;
  violation_pct?: number;
  sample_violation_rows: Record<string, any>[];
  severity: 'high' | 'medium' | 'low';
  action_suggested: 'flag' | 'remove' | 'annotate';
  error?: string;
}

export interface VendorVariant {
  unit_id: string;
  column: string;
  value: string;
  count: number;
}

export interface GlobalVendorCluster {
  cluster_id: string;
  canonical_name: string;
  cluster_size: number;
  member_units: string[];
  variants: VendorVariant[];
  confidence: number;
  source: 'rapidfuzz' | 'difflib';
}

export interface CrossUnitFindings {
  version: number;
  ranAt: string;
  durationMs: number;
  degraded: boolean;
  error?: string | null;
  fkRelationships: FkRelationship[];
  orphans: OrphanFinding[];
  masterViolations: MasterViolationFinding[];
  compoundRuleViolations: CompoundRuleViolation[];
  globalVendorMaster: { canonical_vendors: GlobalVendorCluster[]; proposal_count: number };
  rollup: {
    relationships_inferred: number;
    orphans_total: number;
    orphans_findings: number;
    master_violations_total: number;
    master_violations_findings: number;
    compound_violations_total: number;
    compound_violations_findings: number;
    vendor_clusters_proposed: number;
  };
}

export interface CleaningResultsV2 {
  version: 2;
  sessionId: number | null;
  pipeline?: 'tabular' | 'prose' | 'mixed' | string;
  units: SheetUnit[];
  rollup: RollupStats;
  smeNotesProvided: boolean;
  smeFilesUsed: string[];
  smeDerivedInsights: string[];
  consolidatedWorkbookUrl: string | null;
  classification?: any;
  summary: string;
  cleaningLog: string;
  changeLog: any[];
  legacy_v1: boolean;
  originalFileNames: string[];
  crossUnitFindings: CrossUnitFindings | null;
}

export function adaptToCleaningResultsV2(raw: any): CleaningResultsV2 {
  if (!raw) {
    return {
      version: 2,
      sessionId: null,
      pipeline: 'tabular',
      units: [],
      rollup: {
        total_units: 0, total_sheets: 0, total_rows: 0, total_rows_after: 0,
        qualityScoreBefore_weighted: 0, qualityScoreAfter_weighted: 0,
        issues_total: 0, questions_total: 0,
      },
      smeNotesProvided: false,
      smeFilesUsed: [],
      smeDerivedInsights: [],
      consolidatedWorkbookUrl: null,
      summary: '',
      cleaningLog: '',
      changeLog: [],
      legacy_v1: true,
      originalFileNames: [],
      crossUnitFindings: null,
    };
  }

  if (raw.version === 2 && Array.isArray(raw.files)) {
    const units: SheetUnit[] = [];
    for (const file of raw.files) {
      for (const sheet of (file.sheets || [])) {
        units.push({
          unit_id: sheet.unit_id,
          file_name: file.file_name,
          sheet_name: sheet.sheet_name || null,
          sheet_index: sheet.sheet_index || 0,
          status: sheet.status || 'unknown',
          cleanedFilePath: sheet.cleanedFilePath || null,
          cleanedFileS3Key: sheet.cleanedFileS3Key || null,
          cleanedFileUrl: sheet.cleanedFileUrl || null,
          cleanedFileName: sheet.cleanedFileName || null,
          rows_total: sheet.rows_total || 0,
          rows_after: sheet.rows_after || 0,
          columns_processed: sheet.columns_processed || 0,
          stats: sheet.stats || {},
          issuesFound: Array.isArray(sheet.issuesFound) ? sheet.issuesFound : [],
          stakeholderQuestions: Array.isArray(sheet.stakeholderQuestions) ? sheet.stakeholderQuestions : [],
          canonicalMapping: Array.isArray(sheet.canonicalMapping) ? sheet.canonicalMapping : [],
          previewBefore: sheet.previewBefore || '',
          previewAfter: sheet.previewAfter || '',
          qualityScoreBefore: sheet.qualityScoreBefore || 0,
          qualityScoreAfter: sheet.qualityScoreAfter || 0,
          qualityRevisedDown: !!sheet.qualityRevisedDown,
          cleaningLog: sheet.cleaningLog || '',
          pythonCode: sheet.pythonCode || '',
          error: sheet.error || null,
          stderr_tail: sheet.stderr_tail || null,
        });
      }
    }
    return {
      version: 2,
      sessionId: raw.sessionId || null,
      pipeline: raw.pipeline || 'tabular',
      units,
      rollup: raw.rollup || {
        total_units: units.length, total_sheets: units.length,
        total_rows: 0, total_rows_after: 0,
        qualityScoreBefore_weighted: 0, qualityScoreAfter_weighted: 0,
        issues_total: 0, questions_total: 0,
      },
      smeNotesProvided: !!raw.smeNotesProvided,
      smeFilesUsed: Array.isArray(raw.smeFilesUsed) ? raw.smeFilesUsed : [],
      smeDerivedInsights: Array.isArray(raw.smeDerivedInsights) ? raw.smeDerivedInsights : [],
      consolidatedWorkbookUrl: raw.consolidatedWorkbookUrl || null,
      classification: raw.classification || null,
      summary: raw.summary || '',
      cleaningLog: raw.cleaningLog || '',
      changeLog: Array.isArray(raw.changeLog) ? raw.changeLog : [],
      legacy_v1: false,
      originalFileNames: Array.isArray(raw.originalFileNames) ? raw.originalFileNames : [],
      crossUnitFindings: raw.crossUnitFindings || null,
    };
  }

  const stats = raw.stats || {};
  const fileName = (Array.isArray(raw.originalFileNames) && raw.originalFileNames[0])
    || raw.cleanedFileName
    || 'legacy';
  const unit: SheetUnit = {
    unit_id: `${fileName}::__csv__`,
    file_name: fileName,
    sheet_name: null,
    sheet_index: 0,
    status: raw.cleanedFilePath || raw.cleanedFileS3Key ? 'complete' : 'unknown',
    cleanedFilePath: raw.cleanedFilePath || null,
    cleanedFileS3Key: raw.cleanedFileS3Key || null,
    cleanedFileUrl: raw.cleanedFileUrl || null,
    cleanedFileName: raw.cleanedFileName || null,
    rows_total: stats.totalRows || 0,
    rows_after: stats.rowsAfterCleaning || stats.totalRows || 0,
    columns_processed: stats.columnsProcessed || 0,
    stats,
    issuesFound: Array.isArray(raw.issuesFound) ? raw.issuesFound : [],
    stakeholderQuestions: Array.isArray(raw.stakeholderQuestions) ? raw.stakeholderQuestions : [],
    canonicalMapping: Array.isArray(raw.canonicalMapping) ? raw.canonicalMapping : [],
    previewBefore: raw.previewBefore || '',
    previewAfter: raw.previewAfter || '',
    qualityScoreBefore: stats.qualityScoreBefore || raw.qualityScoreBefore || 0,
    qualityScoreAfter: stats.qualityScoreAfter || raw.qualityScoreAfter || 0,
    cleaningLog: raw.cleaningLog || '',
    pythonCode: raw.pythonCode || '',
  };
  return {
    version: 2,
    sessionId: raw.sessionId || null,
    pipeline: raw.pipeline || 'tabular',
    units: [unit],
    rollup: {
      total_units: 1,
      total_sheets: 1,
      total_rows: unit.rows_total,
      total_rows_after: unit.rows_after,
      qualityScoreBefore_weighted: unit.qualityScoreBefore,
      qualityScoreAfter_weighted: unit.qualityScoreAfter,
      issues_total: unit.issuesFound.length,
      questions_total: unit.stakeholderQuestions.length,
    },
    smeNotesProvided: !!raw.smeNotesProvided,
    smeFilesUsed: Array.isArray(raw.smeFilesUsed) ? raw.smeFilesUsed : [],
    smeDerivedInsights: Array.isArray(raw.smeDerivedInsights) ? raw.smeDerivedInsights : [],
    consolidatedWorkbookUrl: null,
    classification: raw.classification || null,
    summary: raw.summary || '',
    cleaningLog: raw.cleaningLog || '',
    changeLog: Array.isArray(raw.changeLog) ? raw.changeLog : [],
    legacy_v1: true,
    originalFileNames: Array.isArray(raw.originalFileNames) ? raw.originalFileNames : [],
    crossUnitFindings: raw.crossUnitFindings || null,
  };
}
